// src/utils/kioskUpdate.js
//
// Aggiornamento dei tablet kiosk SENZA farli uscire dalla modalità kiosk.
//
// PERCHÉ ESISTE QUESTO MODULO
// Prima l'auto-aggiornamento del kiosk chiamava clearAllCaches() (utils/version.js)
// che svuota localStorage, sessionStorage, IndexedDB e disinstalla i service
// worker. Svuotando localStorage si perdevano `kiosk_mode`, `kiosk_device_id` e
// `kiosk_device_name`: dopo il reload il tablet mostrava la schermata di login
// dell'app normale e i dipendenti non potevano più timbrare.
//
// Per prendere un nuovo deploy basta scartare le cache HTTP della Cache API:
// index.html e version.json sono serviti senza cache (vedi firebase.json) e i
// bundle hanno nomi con hash. Tutto il resto dello stato del dispositivo resta.

// Flag: il prossimo avvio segue un reload di aggiornamento voluto da noi.
// In quel caso il fullscreen è stato perso per causa nostra e non ha senso
// coprire lo scanner con l'overlay di blocco: il primo tocco lo ripristina.
const RELOCK_PENDING_KEY = 'kiosk_relock_pending';

// Tentativi di aggiornamento verso una certa build, per evitare loop di reload
// se il server continua a servire il bundle vecchio (cache CDN, deploy parziale).
const UPDATE_ATTEMPT_KEY = 'kiosk_update_attempt';
const MAX_UPDATE_ATTEMPTS = 2;

/** Svuota solo le cache della Cache API. Non tocca storage né service worker. */
export const clearBuildCaches = async () => {
  if (!('caches' in window)) return;
  try {
    const names = await caches.keys();
    await Promise.all(names.map(name => caches.delete(name)));
  } catch (error) {
    console.warn('⚠️ [KIOSK-UPDATE] Pulizia Cache API non riuscita:', error?.message || error);
  }
};

const readAttempt = () => {
  try {
    return JSON.parse(localStorage.getItem(UPDATE_ATTEMPT_KEY)) || null;
  } catch (error) {
    return null;
  }
};

/**
 * Possiamo ancora tentare di aggiornare verso la build `targetTimestamp`?
 * Dopo MAX_UPDATE_ATTEMPTS reload che non hanno portato alla nuova versione
 * smettiamo, finché il server non pubblica una build diversa.
 */
export const canAttemptUpdate = (targetTimestamp) => {
  const attempt = readAttempt();
  if (!attempt || attempt.target !== targetTimestamp) return true;
  return attempt.count < MAX_UPDATE_ATTEMPTS;
};

/** Registra un tentativo di aggiornamento verso `targetTimestamp`. */
export const recordUpdateAttempt = (targetTimestamp) => {
  const attempt = readAttempt();
  const count = attempt && attempt.target === targetTimestamp ? attempt.count + 1 : 1;
  try {
    localStorage.setItem(UPDATE_ATTEMPT_KEY, JSON.stringify({ target: targetTimestamp, count }));
  } catch (error) {
    /* storage pieno o non disponibile: al massimo riproveremo */
  }
};

/** Da chiamare all'avvio: se questa build è già >= all'obiettivo, azzera i tentativi. */
export const clearUpdateAttemptsIfUpToDate = (buildTimestamp) => {
  const attempt = readAttempt();
  if (attempt && buildTimestamp >= attempt.target) {
    localStorage.removeItem(UPDATE_ATTEMPT_KEY);
  }
};

export const markRelockPending = () => {
  try {
    localStorage.setItem(RELOCK_PENDING_KEY, 'true');
  } catch (error) {
    /* ignora */
  }
};

export const isRelockPending = () => {
  try {
    return localStorage.getItem(RELOCK_PENDING_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

export const clearRelockPending = () => {
  try {
    localStorage.removeItem(RELOCK_PENDING_KEY);
  } catch (error) {
    /* ignora */
  }
};
